import { Button, ProgressLinear, useTranslate } from '@athena/ui'

export interface ContinueCardProps {
  practiceTitle: string
  /** Where they stopped, zero-based, or null if they never saved a position. */
  currentQuestionPosition: number | null
  questionTotal: number
  onResume: () => void
  resuming: boolean
}

/**
 * The one thing on the page that is already begun.
 *
 * It sits at the top and carries the only primary button, because a student who
 * left a practice half-finished came back to finish it. Everything else on
 * this screen is a choice; this is the thing they already chose.
 *
 * ── What the bar measures ────────────────────────────────────────────────
 *
 * The question reached, exactly as the runner's own bar does: on question 4 of
 * 18 it is 4/18 full, here and there. It used to count the questions before
 * the current one, so a student who had opened question 1 came back to an
 * empty bar beside "Question 1 of 18" — and then found the runner already a
 * little way along. One definition, so leaving and coming back changes
 * nothing about what the student sees.
 *
 * "Question 1 of 18" is said once, as the bar's label. It was also a line of
 * text directly above it, so the card said the same thing twice. The position
 * is one-based on screen and zero-based underneath, which is the ordinary
 * off-by-one of counting questions for a person rather than an array.
 */
export function ContinueCard({
  practiceTitle,
  currentQuestionPosition,
  questionTotal,
  onResume,
  resuming,
}: ContinueCardProps) {
  const t = useTranslate()
  const reached = Math.min((currentQuestionPosition ?? 0) + 1, questionTotal)

  return (
    <div className="flex flex-col gap-4 rounded-md border-2 border-primary-border bg-primary-light p-6">
      <h3 className="text-h3 font-semibold text-text">{practiceTitle}</h3>

      {questionTotal > 0 ? (
        <ProgressLinear
          value={(reached / questionTotal) * 100}
          label={t('learn.continue.questionProgress', [reached, questionTotal])}
        />
      ) : (
        <p className="text-body text-text-secondary">{t('learn.continue.readyToResume')}</p>
      )}

      <div>
        <Button size="lg" onClick={onResume} loading={resuming} loadingText={t('common.opening')}>
          {t('learn.continue.resume')}
        </Button>
      </div>
    </div>
  )
}
